import { Button, Code, Table } from "@mantine/core";

import type { LedgerEntryView } from "../../api/generated/model";
import { formatDateTime } from "../../ui/format";

/** A signed change to `available`, as the ledger records it: "+5", "-20", "0". */
const signed = (credits: number) => (credits > 0 ? `+${credits}` : String(credits));

/**
 * What an entry did, in words. A hold takes a job's maximum from available; a capture charges the
 * job and gives the unused rest back, so its change to available is often 0 while it charged the
 * whole hold: the charge comes from the server's `charged`, never from the change.
 */
export function entryText(entry: LedgerEntryView): string {
  switch (entry.kind) {
    case "hold":
      return `Hold ${-entry.amount}`;
    case "release":
      return `Release ${entry.amount}`;
    case "capture":
      if (entry.charged === null) return "Charge";
      // A change of 0 or more: the hold covered the whole charge.
      const fromHold = entry.amount >= 0 && entry.charged > 0;
      return `Charge ${entry.charged}${fromHold ? " (from the hold)" : ""}`;
    case "grant":
      return "Grant";
    case "purchase":
      return "Purchase";
    case "expire":
      return "Expired";
    case "adjust":
      return "Adjustment";
    // A captured job's charge given back to its lots (billing spec ledger-refund).
    case "refund":
      return `Refund ${entry.amount}`;
    // Credits a refunded Stripe payment took back from its lots (billing spec B2-7a).
    case "revoke":
      return `Taken back ${-entry.amount}`;
    // Credits a disputed Stripe payment lost, from its lots and owed (billing spec B2-7c).
    case "dispute":
      return `Disputed ${-entry.amount}`;
  }
}

export interface LedgerTableProps {
  entries: LedgerEntryView[];
  /** Fills the cancel form with a job whose hold is still open. */
  onCancelJob: (jobId: string) => void;
}

/** The team's newest ledger entries, each with its job where it has one (A7). */
export function LedgerTable({ entries, onCancelJob }: LedgerTableProps) {
  // A job charged or released has ended: only an open hold's job can still be canceled.
  const settled = new Set(
    entries.filter((entry) => entry.kind !== "hold" && entry.job_id !== null).map((e) => e.job_id),
  );
  return (
    <Table.ScrollContainer minWidth={760}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>When</Table.Th>
            <Table.Th>Entry</Table.Th>
            <Table.Th>Available</Table.Th>
            <Table.Th>Job</Table.Th>
            <Table.Th>By</Table.Th>
            <Table.Th>Reason</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {entries.map((entry) => (
            <Table.Tr key={entry.id}>
              <Table.Td>{formatDateTime(entry.created_at)}</Table.Td>
              <Table.Td>{entryText(entry)}</Table.Td>
              <Table.Td>{signed(entry.amount)}</Table.Td>
              <Table.Td>
                {entry.job_id === null ? null : (
                  <JobCell
                    jobId={entry.job_id}
                    open={entry.kind === "hold" && !settled.has(entry.job_id)}
                    onCancelJob={onCancelJob}
                  />
                )}
              </Table.Td>
              <Table.Td>{entry.actor_kind}</Table.Td>
              <Table.Td>{entry.reason ?? ""}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

/** The entry's job id, which staff can copy; an open hold's job can be put in the cancel form. */
function JobCell(props: { jobId: string; open: boolean; onCancelJob: (jobId: string) => void }) {
  const { jobId, open, onCancelJob } = props;
  return (
    <>
      <Code>{jobId}</Code>
      {open ? (
        <Button
          variant="subtle"
          size="compact-xs"
          color="red"
          aria-label={`Cancel job ${jobId}`}
          onClick={() => onCancelJob(jobId)}
        >
          Cancel this job
        </Button>
      ) : null}
    </>
  );
}
